import { useEffect } from 'react';
import { AppState, Platform } from 'react-native';
import { useQueryClient } from '@tanstack/react-query';
import { promotionRefreshSeconds } from '../helpers/missionPromotions';
import { refreshMissionReferrals } from './mission-referrals-hook';

const missionQueryRoots = new Set(['customMap', 'branchDetailQuery', 'myMissions', 'myMissionsDetail', 'remoteMissions', 'remoteMission', 'missionReferral', 'missionReferrals']);

/** Refresh cached mission data without changing discovery keys, selection, or the mounted map. */
export default function useMissionPromotionRefresh() {
  const client = useQueryClient();
  useEffect(() => {
    const cache = client.getQueryCache();
    const handled = new Map<string, number>();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let disposed = false;
    const isMissionQuery = (query: { queryKey: readonly unknown[] }) => missionQueryRoots.has(String(query.queryKey[0]));
    const schedule = () => {
      clearTimeout(timer);
      if (disposed) return;
      const now = Date.now();
      let next = Infinity;
      const due = new Set<string>();
      for (const query of cache.getAll().filter(isMissionQuery)) {
        const updatedAt = query.state.dataUpdatedAt;
        if (handled.get(query.queryHash) === updatedAt) continue;
        const seconds = promotionRefreshSeconds(query.state.data);
        if (seconds === undefined) continue;
        const boundary = updatedAt + seconds * 1000 + 250;
        if (boundary <= now) { due.add(query.queryHash); handled.set(query.queryHash, updatedAt); }
        else next = Math.min(next, boundary);
      }
      if (due.size) {
        // Cache notifications are synchronous; defer invalidation to avoid re-entering the scheduler.
        void Promise.resolve().then(() => {
          if (disposed) return;
          void client.invalidateQueries({ predicate: query => due.has(query.queryHash) });
          refreshMissionReferrals();
        });
      }
      if (Number.isFinite(next)) timer = setTimeout(schedule, Math.min(next - now, 86_400_000));
    };
    const unsubscribe = cache.subscribe(event => {
      if (!isMissionQuery(event.query)) return;
      if (event.type === 'removed') handled.delete(event.query.queryHash);
      if (event.type === 'removed' || event.type === 'updated') schedule();
    });
    const foreground = () => {
      void client.invalidateQueries({ predicate: isMissionQuery });
      refreshMissionReferrals();
      schedule();
    };
    const subscription = AppState.addEventListener('change', state => { if (state === 'active') foreground(); });
    const browser = Platform.OS === 'web' && typeof window !== 'undefined' ? window : undefined;
    browser?.addEventListener('focus', foreground);
    schedule();
    return () => { disposed = true; clearTimeout(timer); unsubscribe(); subscription.remove(); browser?.removeEventListener('focus', foreground); };
  }, [client]);
}
